/**
 * "Family account" block of the payers step (#2261). Offers, in this order:
 * the family accounts that share a parent with the child (cards), any other
 * family account of the organisation (searchable picker), and "create a new
 * family account". The parent component owns the selection.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useLingui } from "@lingui/react";
import { Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  FAMILY_GROUP_PICKER_PAGE_SIZE,
  useFamilyGroupPicker,
} from "@/hooks/useFamilyGroups";
import type { FamilyGroupPickerItem } from "@/types/billing";
import { FamilyAccountPicker, FamilyAccountRow } from "./FamilyAccountPicker";

export interface FamilyAccountChooserProps {
  childId?: string;
  /** Id of the chosen existing family group; undefined means "create new". */
  selectedId?: string;
  /** Family name of the chosen group, for the picker trigger before rows load. */
  selectedName?: string;
  /**
   * Select the single shared-parent suggestion once it has loaded. The
   * caller turns this off when the user already chose (or a group is set).
   */
  autoPreselect: boolean;
  /** `auto` is true for the automatic preselect, false for a user choice. */
  onSelectExisting: (item: FamilyGroupPickerItem, auto: boolean) => void;
  onCreateNew: () => void;
}

const cardClass =
  "w-full rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function FamilyAccountChooser({
  childId,
  selectedId,
  selectedName,
  autoPreselect,
  onSelectExisting,
  onCreateNew,
}: FamilyAccountChooserProps) {
  const { i18n } = useLingui();
  const headingId = useId();
  const pickerId = useId();
  // The row last chosen in the dropdown: keeps the trigger label and balance
  // when that row is not on the picker's current result page.
  const [pickedItem, setPickedItem] = useState<FamilyGroupPickerItem | undefined>();

  // Same key as the picker's first page, so both share one request.
  const suggestionsQuery = useFamilyGroupPicker(
    { childId, limit: FAMILY_GROUP_PICKER_PAGE_SIZE },
    !!childId,
  );
  const suggestions = childId
    ? (suggestionsQuery.data?.data ?? []).filter((item) => item.sharesParent)
    : [];
  const suggestionsSettled =
    !!childId && suggestionsQuery.isSuccess && !suggestionsQuery.isPlaceholderData;
  const onlySuggestion = suggestions.length === 1 ? suggestions[0] : undefined;

  const preselectDoneRef = useRef(false);
  useEffect(() => {
    if (!autoPreselect || preselectDoneRef.current) return;
    if (!suggestionsSettled || !onlySuggestion) return;
    preselectDoneRef.current = true;
    onSelectExisting(onlySuggestion, true);
  }, [autoPreselect, suggestionsSettled, onlySuggestion, onSelectExisting]);

  const selectedIsSuggestion =
    !!selectedId && suggestions.some((item) => item.id === selectedId);
  const pickerValue = selectedId && !selectedIsSuggestion ? selectedId : undefined;
  const pickerSelectedItem =
    pickerValue && pickedItem?.id === pickerValue ? pickedItem : undefined;
  const createNewSelected = !selectedId;

  return (
    <div role="group" aria-labelledby={headingId} className="space-y-3">
      <div>
        <div id={headingId} className="text-sm font-medium">
          {i18n._({ id: "contracts.familyAccount.title", message: "Family account" })}
        </div>
        <p className="text-xs text-muted-foreground">
          {i18n._({
            id: "contracts.familyAccount.description",
            message:
              "Bill this contract to an existing family account, or create a new one.",
          })}
        </p>
      </div>

      {suggestions.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-muted-foreground">
            {i18n._({
              id: "contracts.familyAccount.suggestions",
              message: "Family accounts of the same parents",
            })}
          </div>
          {suggestions.map((item) => {
            const selected = item.id === selectedId;
            return (
              // A div, not a <button>: the row holds block content (div, dl),
              // which a button may not contain.
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                data-testid={`family-account-suggestion-${item.id}`}
                onClick={() => onSelectExisting(item, false)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectExisting(item, false);
                  }
                }}
                className={cn(
                  cardClass,
                  "cursor-pointer",
                  selected && "border-primary bg-primary/5"
                )}
              >
                <FamilyAccountRow item={item} selected={selected} />
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-1">
        <Label htmlFor={pickerId} className="text-xs text-muted-foreground">
          {suggestions.length > 0
            ? i18n._({
                id: "contracts.familyAccount.otherAccount",
                message: "Other family account",
              })
            : i18n._({
                id: "contracts.familyAccount.existingAccount",
                message: "Existing family account",
              })}
        </Label>
        <FamilyAccountPicker
          triggerId={pickerId}
          childId={childId}
          // The selected account is shown by its card or the trigger; keep it
          // out of the list so it never appears twice.
          excludeFamilyGroupId={selectedId}
          value={pickerValue}
          selectedItem={pickerSelectedItem}
          selectedFallbackName={pickerValue ? selectedName : undefined}
          onSelect={(item) => {
            setPickedItem(item);
            onSelectExisting(item, false);
          }}
        />
      </div>

      <button
        type="button"
        aria-pressed={createNewSelected}
        data-testid="family-account-create-new"
        onClick={onCreateNew}
        className={cn(
          cardClass,
          "flex items-center gap-2 text-sm",
          createNewSelected && "border-primary bg-primary/5",
        )}
      >
        <Plus className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="font-medium">
          {i18n._({
            id: "contracts.familyAccount.createNew",
            message: "Create a new family account",
          })}
        </span>
      </button>
    </div>
  );
}
